import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { SiteHeader } from "@/components/brand/Brand";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { useAuth, HOME_BY_ROLE } from "@/lib/auth";
import { useI18n } from "@/lib/i18n/i18n";

const Shell = ({ title, children }) => (
  <div className="min-h-screen bg-slate-50" data-testid="password-page">
    <SiteHeader />
    <main className="mx-auto max-w-md px-5 py-16">
      <div className="rounded-[28px] border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="password-title">{title}</h1>
        {children}
      </div>
    </main>
  </div>
);

const RequestForm = () => {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (err) { toast.error(t(apiError(err).message)); } finally { setBusy(false); }
  };
  return (
    <Shell title={t("Recuperar contraseña")}>
      {sent ? (
        <p className="mt-4 flex gap-2 text-sm text-slate-600" data-testid="forgot-sent"><Mail className="h-4 w-4 shrink-0 text-upv" /> {t("Si existe una cuenta con ese email, recibirás un enlace para restablecer la contraseña. Caduca en 1 hora.")}</p>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("Email")} className="h-12 rounded-xl" data-testid="forgot-email-input" />
          <button disabled={busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-upv font-bold text-white hover:bg-upv-dark disabled:opacity-70" data-testid="forgot-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("Enviar enlace")}</button>
        </form>
      )}
      <Link to="/login" className="mt-6 block text-sm font-semibold text-upv" data-testid="password-back-login">{t("Volver a iniciar sesión")}</Link>
    </Shell>
  );
};

const ResetForm = ({ token }) => {
  const { t } = useI18n();
  const nav = useNavigate();
  const { startSession } = useAuth();
  const [info, setInfo] = useState(undefined);
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/auth/reset/${token}`).then((r) => setInfo(r.data)).catch(() => setInfo(null)); }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) { toast.error(t("Las contraseñas no coinciden")); return; }
    setBusy(true);
    try { const { data } = await api.post("/auth/reset", { token, password: pw.a }); startSession(data); toast.success(t("Contraseña actualizada")); nav(HOME_BY_ROLE[data.user.role] || "/"); } catch (err) { toast.error(t(apiError(err).message)); setBusy(false); }
  };
  if (info === undefined) return <Shell title={t("Restablecer contraseña")}><Loader2 className="mt-6 h-5 w-5 animate-spin text-slate-400" /></Shell>;
  if (!info) return <Shell title={t("Restablecer contraseña")}><p className="mt-4 text-sm text-slate-600" data-testid="reset-invalid">{t("Enlace no válido, caducado o ya utilizado")}</p><Link to="/recuperar" className="mt-6 block text-sm font-semibold text-upv">{t("Pedir un enlace nuevo")}</Link></Shell>;
  return (
    <Shell title={t("Restablecer contraseña")}>
      <p className="mt-2 text-sm text-slate-500" data-testid="reset-email">{info.email}</p>
      <form onSubmit={submit} className="mt-6 space-y-3">
        <Input type="password" autoComplete="new-password" value={pw.a} onChange={(e) => setPw((p) => ({ ...p, a: e.target.value }))} placeholder={t("Nueva contraseña")} className="h-12 rounded-xl" data-testid="reset-password-input" />
        <Input type="password" autoComplete="new-password" value={pw.b} onChange={(e) => setPw((p) => ({ ...p, b: e.target.value }))} placeholder={t("Repite la contraseña")} className="h-12 rounded-xl" data-testid="reset-password2-input" />
        <p className="text-xs text-slate-400">{t("Mínimo 8 caracteres, con letras y números.")}</p>
        <button disabled={busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-upv font-bold text-white hover:bg-upv-dark disabled:opacity-70" data-testid="reset-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("Guardar contraseña")}</button>
      </form>
    </Shell>
  );
};

export default function PasswordReset() {
  const { token } = useParams();
  return token ? <ResetForm token={token} /> : <RequestForm />;
}
